import React, { useEffect, useMemo, useRef, useState } from 'react';

import Helmet from 'react-helmet';
import { Grid, InputAdornment } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { getQueryParams } from 'utils';
import { authModel } from 'models';
import { AUTH_HEADERS } from 'models/constants';
import { ROUTES } from 'routes/constants';
import { AccountCard, ButtonComponent } from 'components';
import { COLORS } from 'theme/constants';
import { SocialAccount } from 'types';
import {
  Box,
  BuilderAuthCard,
  BuilderSearchField,
  BuilderSubtitle,
  BuilderTitle,
  ScrollableList,
} from 'pages/auth/style';
import { redirectToSignIn } from 'utils/helpers/urlHelpers';
import { ReactComponent as SearchIcon } from 'assets/icons/system/search-icon.svg';
import { SEARCH_THRESHOLD, filterAccounts } from 'pages/auth/SelectAccountPage/helpers';
import { SPACE_CARD_ATTRIBUTE, useVisibleItemsHeight } from 'pages/auth/SelectAccountPage/useVisibleItemsHeight';

import { BuilderContactUsFooter } from '../BuilderContactUsFooter';

const BuilderSelectAccountPage = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [preAuthToken, setPreAuthToken] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredAccounts = useMemo(() => filterAccounts(accounts, searchTerm), [accounts, searchTerm]);
  const listRef = useRef<HTMLDivElement>(null);
  const listMaxHeight = useVisibleItemsHeight(listRef, accounts.length, SEARCH_THRESHOLD);

  useEffect(() => {
    const preAuthToken = getQueryParams().pre_auth_token;

    if (preAuthToken && typeof preAuthToken === 'string') {
      setPreAuthToken(preAuthToken);
      authModel.listAccounts({ token: preAuthToken }).then(response => {
        if (response.pubtoolToken) {
          localStorage.setItem(AUTH_HEADERS.PUBTOOL_TOKEN, response.pubtoolToken);
          history.push(ROUTES.home);
        } else if (response.accounts.length === 0)
          history.push({
            pathname: ROUTES.authGoogleAccountNotFound,
            search: `?pre_auth_token=${preAuthToken}`,
          });
        else setAccounts(response.accounts);
      });
    } else redirectToSignIn();
  }, []);

  const onSelectAccount = (account: any) => {
    authModel
      .socialSignIn({
        id: account.id,
        token: preAuthToken,
      })
      .then(response => {
        if (response.pubtoolToken) {
          localStorage.setItem(AUTH_HEADERS.PUBTOOL_TOKEN, response.pubtoolToken);
          history.push(ROUTES.home);
        }
      });
  };

  if (accounts.length === 0) return null;

  return (
    <BuilderAuthCard>
      <Helmet title={t('pages.auth.selectAccountPage.tabTitle')} />
      <Grid container direction="column" alignItems="center">
        <Grid item xl={7} style={{ flex: 1 }}>
          <Box mb={6}>
            <ButtonComponent
              color="primary"
              variant="text"
              className="no-padding"
              text="< Back"
              onClick={redirectToSignIn}
              style={{ color: COLORS.builderBrandColor }}
            />
          </Box>
          <Box mb={8}>
            <BuilderTitle>Select your World</BuilderTitle>
          </Box>
          {accounts.length > SEARCH_THRESHOLD && (
            <Box mb={2}>
              <BuilderSearchField
                variant="outlined"
                fullWidth
                size="small"
                value={searchTerm}
                placeholder={t('pages.auth.selectAccountPage.searchPlaceholder')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value)}
              />
            </Box>
          )}
        </Grid>
        <Grid item xl={7} container direction="column" style={{ flex: 1 }}>
          <ScrollableList ref={listRef} maxHeight={listMaxHeight}>
            {filteredAccounts.map(account => (
              <Grid item key={account.client} {...{ [SPACE_CARD_ATTRIBUTE]: true }}>
                <AccountCard
                  className="builder"
                  title={account.client}
                  text={account.email}
                  onClick={() => onSelectAccount(account)}
                />
              </Grid>
            ))}
            {filteredAccounts.length === 0 && (
              <BuilderSubtitle>{t('pages.auth.selectAccountPage.noResults')}</BuilderSubtitle>
            )}
          </ScrollableList>
        </Grid>
      </Grid>
      <BuilderContactUsFooter />
    </BuilderAuthCard>
  );
};

export default BuilderSelectAccountPage;
